import { useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useState } from 'react'
import StructuredData from '../components/StructuredData'
import './CaseworkBlueprintsLandingPage.css'

interface QuoteFormData {
  name: string
  email: string
  phone: string
  company?: string
  manufacturer?: string
  projectDescription?: string
}

function CaseworkBlueprintsLandingPage() {
  const formRef = useRef<HTMLDivElement>(null)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubmitError, setIsSubmitError] = useState(false)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<QuoteFormData>()

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const FORMSPREE_ENDPOINT = 'https://formspree.io/f/mojnjjob'

  const onSubmit = async (data: QuoteFormData) => {
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          phone: data.phone,
          company: data.company || '',
          manufacturer: data.manufacturer || '',
          projectDescription: data.projectDescription || '',
          _subject: 'Casework Blueprint & Spec Request',
        }),
      })
      if (!res.ok) throw new Error('Submit failed')
      setIsSubmitted(true)
      reset()
      setTimeout(() => setIsSubmitted(false), 8000)
    } catch {
      setIsSubmitError(true)
      setTimeout(() => setIsSubmitError(false), 5000)
    }
  }

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Lab Casework Blueprint & Technical Specification Services',
    description:
      'Professional blueprint drafting and technical specification development for custom laboratory casework. We translate your lab requirements into manufacturer-ready drawings.',
    provider: { '@type': 'Organization', name: 'Laboratory Specialists' },
    serviceType: 'Laboratory Design & Specification',
  }

  return (
    <div className="cbp-landing-page">
      <StructuredData data={structuredData} />

      {/* Hero */}
      <section className="cbp-hero">
        <div className="cbp-hero-bg" aria-hidden />
        <div className="container cbp-hero-inner">
          <p className="cbp-hero-eyebrow">Referred by your casework manufacturer?</p>
          <h1 className="cbp-hero-title">
            We Create the Blueprints and Specs Your Manufacturer Needs to Build Your Custom Casework
          </h1>
          <p className="cbp-hero-subtitle">
            You know what your lab needs. We translate that into precise technical drawings and specifications—so your manufacturer can build it exactly right, the first time.
          </p>
          <div className="cbp-hero-cta">
            <button type="button" className="cbp-cta-button" onClick={scrollToForm}>
              Request a Free Consultation
            </button>
            <p className="cbp-hero-cta-note">Response within 24 hours · No obligation</p>
          </div>
        </div>
      </section>

      {/* Problem / value prop */}
      <section className="cbp-value">
        <div className="container">
          <h2 className="cbp-value-title">Why Manufacturers Refer Clients to Us</h2>
          <p className="cbp-value-intro">
            Casework manufacturers build to spec—but most clients don't arrive with one. Without accurate blueprints, custom casework projects stall, go over budget, or get built wrong. We close that gap.
          </p>
          <div className="cbp-value-grid">
            <div className="cbp-value-card">
              <div className="cbp-value-icon" aria-hidden>📐</div>
              <h3>Manufacturer-Ready Drawings</h3>
              <p>
                We produce detailed CAD drawings and dimensioned floor plans your manufacturer can build from directly—no back-and-forth, no guesswork.
              </p>
            </div>
            <div className="cbp-value-card">
              <div className="cbp-value-icon" aria-hidden>🔬</div>
              <h3>Lab-Specific Expertise</h3>
              <p>
                We understand lab workflows, chemical compatibility, utility rough-ins, and ADA requirements. Your specs won't just be drawn—they'll be correct for laboratory use.
              </p>
            </div>
            <div className="cbp-value-card">
              <div className="cbp-value-icon" aria-hidden>⚡</div>
              <h3>Faster Project Start</h3>
              <p>
                Manufacturers can't quote or build without accurate specs. We get those specs done quickly so your project timeline doesn't slip before it even begins.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="cbp-process">
        <div className="container">
          <h2 className="cbp-process-title">How It Works</h2>
          <ol className="cbp-process-steps">
            <li>
              <span className="cbp-step-number">1</span>
              <div>
                <strong>Tell us about your lab</strong>
                <p>We start with a free consultation to understand your space, workflow, and casework requirements. No drawings needed—just your vision.</p>
              </div>
            </li>
            <li>
              <span className="cbp-step-number">2</span>
              <div>
                <strong>We draft the blueprints and technical specs</strong>
                <p>Our team produces dimensioned floor plans, elevation drawings, and material specifications tailored to your lab and compatible with your chosen manufacturer's production process.</p>
              </div>
            </li>
            <li>
              <span className="cbp-step-number">3</span>
              <div>
                <strong>Hand off to your manufacturer</strong>
                <p>You receive a complete spec package to take directly to your casework manufacturer—ready for quoting and fabrication.</p>
              </div>
            </li>
          </ol>
        </div>
      </section>

      {/* Who this is for */}
      <section className="cbp-audience">
        <div className="container">
          <h2 className="cbp-audience-title">Who We Work With</h2>
          <ul className="cbp-audience-list">
            <li>University and research labs designing new or renovated spaces</li>
            <li>Biotech and pharmaceutical companies expanding lab capacity</li>
            <li>K–12 and community colleges building science classrooms</li>
            <li>Healthcare and clinical labs requiring code-compliant casework layouts</li>
            <li>Any client referred by a casework manufacturer who needs drawings before fabrication can begin</li>
          </ul>
        </div>
      </section>

      {/* Form */}
      <section className="cbp-form-section" ref={formRef} id="quote-form">
        <div className="container">
          <div className="cbp-form-card">
            <h2 className="cbp-form-title">Request a Free Consultation</h2>
            <p className="cbp-form-lead-magnet">
              Tell us about your project. We'll follow up within 24 hours to discuss your casework needs and next steps.
            </p>

            {isSubmitted ? (
              <div className="cbp-form-success">
                <h3>Thanks—we'll be in touch within 24 hours.</h3>
                <p>We'll reach out to schedule your consultation and discuss your blueprint requirements.</p>
              </div>
            ) : (
              <form className="cbp-form" onSubmit={handleSubmit(onSubmit)}>
                {isSubmitError && (
                  <p className="cbp-form-error" role="alert">
                    Something went wrong. Please try again or call us at (541) 658-9374.
                  </p>
                )}

                <div className="cbp-form-group">
                  <label htmlFor="cbp-name">
                    Full Name <span className="cbp-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="cbp-name"
                    {...register('name', {
                      required: 'Name is required',
                      minLength: { value: 2, message: 'Please enter your full name' },
                    })}
                    aria-invalid={errors.name ? 'true' : 'false'}
                    placeholder="First and Last Name"
                  />
                  {errors.name && (
                    <span className="cbp-error" role="alert">{errors.name.message}</span>
                  )}
                </div>

                <div className="cbp-form-group">
                  <label htmlFor="cbp-email">
                    Email <span className="cbp-required">*</span>
                  </label>
                  <input
                    type="email"
                    id="cbp-email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Please enter a valid email',
                      },
                    })}
                    aria-invalid={errors.email ? 'true' : 'false'}
                    placeholder="Email Address"
                  />
                  {errors.email && (
                    <span className="cbp-error" role="alert">{errors.email.message}</span>
                  )}
                </div>

                <div className="cbp-form-group">
                  <label htmlFor="cbp-phone">
                    Phone <span className="cbp-required">*</span>
                  </label>
                  <input
                    type="tel"
                    id="cbp-phone"
                    {...register('phone', {
                      required: 'Phone is required',
                      pattern: {
                        value: /^[\d\s\-+()]+$/,
                        message: 'Please enter a valid phone number',
                      },
                    })}
                    aria-invalid={errors.phone ? 'true' : 'false'}
                    placeholder="123-456-7890"
                  />
                  {errors.phone && (
                    <span className="cbp-error" role="alert">{errors.phone.message}</span>
                  )}
                </div>

                <div className="cbp-form-group">
                  <label htmlFor="cbp-company">Company / Institution (optional)</label>
                  <input
                    type="text"
                    id="cbp-company"
                    {...register('company')}
                    placeholder="Your lab or organization"
                  />
                </div>

                <div className="cbp-form-group">
                  <label htmlFor="cbp-manufacturer">Which manufacturer referred you? (optional)</label>
                  <input
                    type="text"
                    id="cbp-manufacturer"
                    {...register('manufacturer')}
                    placeholder="Manufacturer name"
                  />
                </div>

                <div className="cbp-form-group">
                  <label htmlFor="cbp-projectDescription">Brief project description (optional)</label>
                  <textarea
                    id="cbp-projectDescription"
                    {...register('projectDescription')}
                    rows={3}
                    placeholder="e.g. 12-bench chemistry lab renovation, need metal casework drawings for 2,400 sq ft space"
                  />
                </div>

                <p className="cbp-privacy">
                  By submitting you agree to our{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>
                  . We won't share your information.
                </p>

                <button type="submit" className="cbp-submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Request My Free Consultation'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Legal */}
      <section className="cbp-legal">
        <div className="container">
          <p className="cbp-legal-text">
            Laboratory Specialists. Blueprint drafting and technical specification services for custom laboratory casework.{' '}
            <a href="/privacy">Privacy Policy</a> · <a href="/contact">Contact</a>
          </p>
        </div>
      </section>
    </div>
  )
}

export default CaseworkBlueprintsLandingPage
